import React, { useState } from 'react';
import { Box, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { DrawerContentProps } from './DrawerContent.props';
import { MainMenuItem, SubMenuItem, ChildMenuItem } from '..';
import { getIconForSection } from './iconMap';

const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: '8px',
  px: '14px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#cbd5e1',
  cursor: 'pointer',
  transition: 'background .15s',
  userSelect: 'none',
  '&:hover': { bgcolor: '#0f172a', color: '#fff' },
} as const;

const navItemBaseSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: '6px',
  px: '14px',
  pl: '30px',
  borderLeft: '3px solid transparent',
  fontSize: '14px',
  color: '#cbd5e1',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background .15s',
  '&:hover': { bgcolor: '#0f172a', color: '#fff' },
} as const;

const activeItemSx = {
  bgcolor: 'v4.sidebarActive',
  borderLeftColor: 'v4.accent',
  color: '#fff',
} as const;

const iconSx = { fontSize: 20 } as const;
const chevronSx = (isExpanded: boolean) => ({
  fontSize: 16,
  color: '#94a3b8',
  ml: 'auto',
  transition: 'transform 0.2s ease',
  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
}) as const;

const childLabelSx = {
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#94a3b8',
  px: '14px',
  pl: '30px',
  pt: '8px',
  pb: '2px',
  fontWeight: 600,
} as const;

/** Build initial expanded state: expand sections that contain the active route. */
const buildInitialExpanded = (menuData: MainMenuItem[], pathname: string) => {
  const expanded: Record<string, boolean> = {};
  for (const item of menuData) {
    if (!item.subMenuItems?.length) continue;
    const hasActive = item.subMenuItems.some(
      (sub) => sub.url === pathname || sub.subMenuItems?.some((c) => c.url === pathname),
    );
    expanded[item.id] = hasActive;
  }
  return expanded;
};

export const DrawerContent: React.FC<DrawerContentProps> = ({
  menuData,
  handleMenuItemClick,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    () => buildInitialExpanded(menuData, location.pathname),
  );

  const toggleSection = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const isActive = (url: string) =>
    url !== '' && location.pathname === url;

  const handleClick = (url: string) => {
    handleMenuItemClick(true);
    if (window.location.pathname === url) {
      setTimeout(() => navigate('/loader'), 1);
    } else {
      navigate(url);
    }
  };

  const renderNavItem = (name: string, url: string, icon?: React.ReactNode) => (
    <Box
      key={url || name}
      onClick={() => url && handleClick(url)}
      sx={{
        ...navItemBaseSx,
        ...(isActive(url) ? activeItemSx : {}),
      }}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && url && handleClick(url)}
    >
      {icon}
      {name}
    </Box>
  );

  const renderChildItems = (children: ChildMenuItem[]) =>
    children.map((child) => renderNavItem(child.name, child.url));

  const renderSubItems = (subs: SubMenuItem[]) =>
    subs.map((sub) => (
      <React.Fragment key={sub.id}>
        {sub.subMenuItems && sub.subMenuItems.length > 0 ? (
          <>
            <Box sx={childLabelSx}>{sub.name}</Box>
            {renderChildItems(sub.subMenuItems)}
          </>
        ) : (
          renderNavItem(sub.name, sub.url)
        )}
      </React.Fragment>
    ));

  const renderMenuItem = (item: MainMenuItem) => {
    const Icon = getIconForSection(item.name);

    // Top-level item with no children (e.g. Home)
    if (!item.subMenuItems || item.subMenuItems.length === 0) {
      return (
        <Box
          key={item.id}
          onClick={() => item.url && handleClick(item.url)}
          sx={{
            ...sectionHeaderSx,
            borderLeft: '3px solid transparent',
            ...(isActive(item.url ?? '') ? activeItemSx : {}),
          }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && item.url && handleClick(item.url)}
        >
          <Icon sx={iconSx} />
          {item.name}
        </Box>
      );
    }

    // Collapsible section
    const isExpanded = expanded[item.id] ?? false;
    return (
      <Box key={item.id} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', py: 0.25 }}>
        <Box
          onClick={() => toggleSection(item.id)}
          sx={sectionHeaderSx}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection(item.id)}
        >
          <Icon sx={iconSx} />
          {item.name}
          <ChevronRightIcon sx={chevronSx(isExpanded)} />
        </Box>
        <Collapse in={isExpanded} timeout={200}>
          {renderSubItems(item.subMenuItems)}
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{ overflowY: 'auto', flex: 1 }}
    >
      {menuData.map((item) => (
        <React.Fragment key={item.id}>{renderMenuItem(item)}</React.Fragment>
      ))}
    </Box>
  );
};
